<script lang="ts">
  import type { CommentAuthorModel, CommentModel } from "../comments/CommentTypes";
  import { getContext, onMount } from "svelte";
  import { getAutosaveFromDb, removeAutosaveFromDb, storeAutosaveInDb } from "../../common/indexedDB";
  import type { Writable } from "svelte/store";
  import type { CommentsStateType } from "./MaterialComments.svelte";
  import Api from "../../api";
  import { showToast } from "../toast";
  import { NotificationPermissionRequestType, promptNotificationsPermission } from "../../common/cloud-messaging";
  import { captureFetchException, wait } from "../../common/helpers";
  import AuthDialog from "../Auth/AuthDialog";
  import CommentsEditor from "./editor/CommentsEditor.svelte";
  import cn from "classnames";
  import css from '../../../css/comments.pcss.json'
  import buttonsCss from '../../../css/v9/buttons.pcss.json'
  import SVG from '../SVG_svelte.svelte'

  export let pid: number = 0
  export let shown: boolean = true
  export let open: boolean = false
  export let showCancel: boolean = false
  export let targetId: string | number;
  export let targetType: string;
  export let action: string = null;
  export let comment: CommentModel = null;
  // noinspection IncorrectFormatting
  export let onFormSubmit: () => void = () => {};

  const state: Writable<CommentsStateType> = getContext('comments')

  let sending: boolean = false;
  let value: string = comment?.original_text ?? null;
  let currentEditor: CommentsEditor;
  let form: HTMLFormElement;
  let openState: boolean = open

  onMount(async () => {
    if (action === 'edit') {
      return
    }
    const autosaveInfo = await getAutosaveFromDb(`comment_${targetType}_${targetId}`)
    if (!autosaveInfo) {
      return
    }
    let pidValue = autosaveInfo.fields.find(field => field[0] === 'pid')[1]
    if (pidValue instanceof File) {
      return
    }
    const savedPid = typeof pidValue === 'string' ? parseInt(pidValue) : pidValue
    if (pid === savedPid) {
      const textValue = autosaveInfo.fields.find(field => field[0] === 'comment_text')[1]
      if (typeof textValue === 'string') {
        value = textValue
        currentEditor?.focus()
      }
    }
  })

  async function handleFormSubmit(event: Event) {
    event.preventDefault()

    const realAction = action ?? 'add'

    if (!$state.isLoggedIn) {
      showAuth()
      return
    }

    const formData = new FormData(form)
    formData.append('pid', pid.toString())
    formData.append('target_id', targetId.toString())
    formData.append('target_type', targetType)

    const valid = await currentEditor?.validate()
    if (!valid) {
      return
    }

    sending = true

    await Api.post({ url: `comments/${realAction}`, body: formData })
      .then((result: {
        status: 0 | 1,
        message: string,
        comment?: CommentModel,
        user?: CommentAuthorModel
      }) => {
        showToast({
          type: result.status === 0 ? 'error' : 'success',
          text: result.message
        })

        if (result.status !== 1) {
          return
        }

        if (result.comment) {
          $state.updateComment(result.comment, result.user)
        }

        if (realAction === 'add') {
          value = ''
          if (!open) {
            openState = false
          }
          currentEditor?.reset()
        }

        removeAutosave()

        if (typeof onFormSubmit === 'function') {
          onFormSubmit()
        }

        promptNotificationsPermission(NotificationPermissionRequestType.comment)
      })
      .catch(error => {
        captureFetchException(error)
        showToast({
          type: 'error',
          text: 'При отправке комментария произошла ошибка'
        })
      })

    sending = false
  }

  function onCancel() {
    removeAutosave()
    currentEditor?.reset()
    if (!open) {
      openState = false
    }
    if (typeof onFormSubmit === 'function') {
      onFormSubmit()
    }
  }

  function removeAutosave() {
    getAutosaveFromDb(`comment_${targetType}_${targetId}`)
      .then(autosaveInfo => {
        if (!autosaveInfo) {
          return
        }
        let pidValue = autosaveInfo.fields.find(field => field[0] === 'pid')[1]
        if (pidValue instanceof File) {
          return
        }
        const savedPid = typeof pidValue === 'string' ? parseInt(pidValue) : pidValue
        if (pid === savedPid) {
          removeAutosaveFromDb(autosaveInfo.key)
        }
      })
  }

  async function openForm() {
    openState = true
    await wait(100)
    currentEditor?.focus()
  }

  function showAuth() {
    const formData = new FormData(form)
    formData.append('pid', pid.toString())
    const fieldsArray = Array.from(formData)
    const fields: [string, FormDataEntryValue][] = []

    fieldsArray.forEach(field => {
      if (typeof field[1] === 'string') {
        fields.push(field)
      }
    })

    storeAutosaveInDb({
      key: `comment_${targetType}_${targetId}`,
      fields,
      saveDate: Date.now() / 1000 | 0
    }).finally(() => {
      AuthDialog.show()
    })
  }

  function triggerSubmit() {
    form?.dispatchEvent(new Event('submit'))
  }

  const uid = `${pid}_${action}`
</script>

{#if shown}
  <div id="comment_form" class={css['comment-form']}>
    <form bind:this={form} on:submit={handleFormSubmit}>
      {#if !openState}
        <button
          type="button"
          class={cn(css['comment-form__textarea-wrapper'], css['comment-form__textarea-wrapper--fake'])}
          on:click={openForm}
        >
          <span class={css['comment-form__placeholder']}>Написать комментарий…</span>
          <!--suppress HtmlUnknownTag -->
          <div class={css['comment-form__textarea-controls']}>
            <div class={css['button']}>
              <SVG i="sg-new/gamepad" adaptive/>
            </div>
            <div class={css['button']}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20.25 4.5H3.75a.75.75 0 0 0-.75.75v13.5c0 .41.34.75.75.75h16.5c.41 0 .75-.34.75-.75V5.25a.75.75 0 0 0-.75-.75Z"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="m3 15.75 4.72-4.72a.75.75 0 0 1 1.06 0l4.19 4.19a.75.75 0 0 0 1.06 0l1.94-1.94a.75.75 0 0 1 1.06 0L21 17.25"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M14.63 10.5a1.13 1.13 0 1 0 0-2.25 1.13 1.13 0 0 0 0 2.25Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div class={css['button']}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10.13 13.5H3.75a.75.75 0 0 1-.75-.75v-6A.75.75 0 0 1 3.75 6h5.63a.75.75 0 0 1 .74.75V15a3.75 3.75 0 0 1-3.74 3.75M21 13.5h-6.38a.75.75 0 0 1-.74-.75v-6a.75.75 0 0 1 .74-.75h5.63a.75.75 0 0 1 .75.75V15a3.75 3.75 0 0 1-3.75 3.75"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class={css['button']}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="m4.5 3.75 15 16.5M14.52 14.77a3.75 3.75 0 0 1-5.04-5.54"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6.94 6.43A12.72 12.72 0 0 0 1.5 12s3 6.75 10.5 6.75c1.76.01 3.5-.4 5.06-1.18M19.56 15.85A12.75 12.75 0 0 0 22.5 12s-3-6.75-10.5-6.75c-.65 0-1.3.05-1.94.16"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12.7 8.32a3.75 3.75 0 0 1 3.03 3.33"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
        </button>
      {:else}
        <div class={css['comment-form__textarea-wrapper']}>
          <CommentsEditor
            bind:this={currentEditor}
            uid={uid}
            sending={sending}
            bind:value={value}
            attachments={comment?.attachments}
            showAuth={triggerSubmit}
          />
          <div class={css['comment-form__buttons']}>
            <button
              type="submit"
              class={cn(buttonsCss['button'], buttonsCss['primary'])}
              disabled={sending}
            >
              Отправить
            </button>
            {#if showCancel}
              <button
                type="button"
                class={cn(buttonsCss['button'], buttonsCss['text'])}
                disabled={sending}
                on:click={onCancel}
              >
                Отмена
              </button>
            {/if}
          </div>
        </div>
      {/if}
    </form>
  </div>
{/if}
